import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { TabChip } from "@/components/TabChip/TabChip";
import { classNames } from "@/utils/classNames";

import {
	useGetHistoryBySubtaskQuery,
	useGetHistoryByTaskQuery,
} from "@/pages/Private/redux/tasks-and-subtasks/task-and-subtasks-activity.api";

import { commentsSelector } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks-activity-slice";

import Activity from "./Activity";
import History from "./History";

interface ActivityTabsProps {
	companyToDoProgressId: number | null;
	isTask: boolean;
}

type TabKey = "comments" | "history";

/**
 * Comments and history side by side instead of one endless column — the thread
 * used to push everything below it off the screen on any busy task.
 */
export default function ActivityTabs({ companyToDoProgressId, isTask }: ActivityTabsProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);
	const [activeTab, setActiveTab] = useState<TabKey>("comments");

	const allComments = useSelector(commentsSelector);

	const commentCount = useMemo(
		() =>
			allComments.filter((comment) =>
				isTask
					? comment.taskId === companyToDoProgressId
					: comment.subtaskId === companyToDoProgressId
			).length,
		[allComments, companyToDoProgressId, isTask]
	);

	// Counting is why this is fetched here too; RTK Query dedupes it with History's
	// own subscription, so the tab badge costs no extra request.
	const { data: taskHistory } = useGetHistoryByTaskQuery(companyToDoProgressId ?? 0, {
		skip: !companyToDoProgressId || !isTask,
	});
	const { data: subtaskHistory } = useGetHistoryBySubtaskQuery(companyToDoProgressId ?? 0, {
		skip: !companyToDoProgressId || isTask,
	});

	const historyCount = ((isTask ? taskHistory : subtaskHistory) ?? []).length;

	const tabs: { key: TabKey; label: string; count: number }[] = [
		{ key: "comments", label: ts("comments"), count: commentCount },
		{ key: "history", label: ts("historyTab"), count: historyCount },
	];

	return (
		<div className="w-full">
			<div className="flex items-center gap-6 border-b border-border" role="tablist">
				{tabs.map((tab) => {
					const isActive = tab.key === activeTab;

					return (
						<button
							key={tab.key}
							aria-selected={isActive}
							className={classNames(
								"flex items-center -mb-px border-b-2 pb-2 pt-1 text-[14px] font-semibold leading-5 transition-colors",
								isActive
									? "border-accent text-primary-blue-dark"
									: "border-transparent text-subtle hover:text-primary-blue-dark"
							)}
							role="tab"
							type="button"
							onClick={() => setActiveTab(tab.key)}
						>
							{tab.label}
							<TabChip active={isActive} label={`${tab.count}`} />
						</button>
					);
				})}
			</div>

			<div className="pt-4">
				{activeTab === "comments" ? (
					<Activity companyToDoProgressId={companyToDoProgressId} isTask={isTask} />
				) : (
					<History companyToDoProgressId={companyToDoProgressId} isTask={isTask} />
				)}
			</div>
		</div>
	);
}
